import request from '../request';
import i18n, { type SupportedLanguage } from '@/locales';

/** 新闻状态：0-下架，1-上架 */
export type NewsStatus = 0 | 1;

export interface NewsRecord {
  id?: number;
  createTime?: string;
  updateTime?: string;
  publishTime?: string;
  title?: string;
  summary?: string;
  coverImg?: string;
  content?: string;
  status?: NewsStatus;
  langExt?: string;
}

export interface NewsPageData {
  records: NewsRecord[];
  total: number;
  size: number;
  current: number;
  pages?: number;
}

export interface NewsPageResult {
  code: number;
  msg: string | null;
  data: NewsPageData;
}

export interface NewsDetailResult {
  code: number;
  msg: string | null;
  data: NewsRecord;
}

export interface NewsPageParams {
  newsId?: number;
  keyword?: string;
  status?: NewsStatus;
  current?: number;
  size?: number;
}

type NewsLanguageKey = 'zh' | 'en' | 'jp';

type NewsLocalizedFields = Partial<
  Pick<NewsRecord, 'title' | 'summary' | 'coverImg' | 'content'>
>;

const newsLanguageMap: Record<SupportedLanguage, NewsLanguageKey> = {
  'zh-CN': 'zh',
  'en-US': 'en',
  'ja-JP': 'jp',
};

function hasValue(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function decodeNewsField(value: string): string {
  if (!value.startsWith('b64:')) return value;

  try {
    const binary = atob(value.slice(4));
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return value;
  }
}

function parseLocalizedFields(langExt?: string): NewsLocalizedFields | undefined {
  if (!hasValue(langExt)) return undefined;

  try {
    const parsed = JSON.parse(langExt) as Record<string, NewsLocalizedFields>;
    const languageKey = newsLanguageMap[
      (i18n.resolvedLanguage || i18n.language || 'zh-CN') as SupportedLanguage
    ] ?? 'zh';
    return parsed[languageKey];
  } catch {
    return undefined;
  }
}

function localizeNewsRecord(record: NewsRecord): NewsRecord {
  const localizedFields = parseLocalizedFields(record.langExt);
  if (!localizedFields) return record;

  return {
    ...record,
    ...(hasValue(localizedFields.title) ? { title: decodeNewsField(localizedFields.title) } : {}),
    ...(hasValue(localizedFields.summary)
      ? { summary: decodeNewsField(localizedFields.summary) }
      : {}),
    ...(hasValue(localizedFields.coverImg)
      ? { coverImg: decodeNewsField(localizedFields.coverImg) }
      : {}),
    ...(hasValue(localizedFields.content)
      ? { content: decodeNewsField(localizedFields.content) }
      : {}),
  };
}

/** POST /jdx/mini/news/page */
export function newsPage(params?: NewsPageParams): Promise<NewsPageResult> {
  return request
    .post<NewsPageResult, NewsPageParams>({
      url: '/jdx/mini/news/page',
      data: {
        current: 1,
        size: 10,
        ...params,
      },
    })
    .then(result => ({
      ...result,
      data: {
        ...result.data,
        records: result.data.records.map(localizeNewsRecord),
      },
    }));
}

/** POST /jdx/mini/news/detail */
export function newsDetail(newsId: number | string): Promise<NewsDetailResult> {
  return request
    .post<NewsDetailResult, { newsId: number }>({
      url: '/jdx/mini/news/detail',
      data: { newsId: Number(newsId) },
    })
    .then(result => ({
      ...result,
      data: localizeNewsRecord(result.data),
    }));
}
